import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { qdfAPI } from '../../../services/api';
import { SC_STATUS_OPTIONS } from '../QDFConstants';
import { AuthContext } from '../../../contexts/AuthContext';
import { Modal, Button, Badge, Input, Textarea, Select, FieldSet } from '../../../components/ui';

const blankSparePart = () => ({ deviceName: '', description: '', quantity: '' });
const hasSparePartValue = (part) =>
  Boolean(part?.deviceName?.trim() || part?.description?.trim() || String(part?.quantity || '').trim());

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toISO = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const buildState = (entry) => ({
  scStatus: entry?.scStatus || '',
  targetResponse: toDateTimeLocal(entry?.targetResponse),
  targetResolution: toDateTimeLocal(entry?.targetResolution),
  actualDate: toDateTimeLocal(entry?.actualDate),
  actualResolution: toDateTimeLocal(entry?.actualResolution),
  spareParts: Array.isArray(entry?.inspection?.spareParts) ? entry.inspection.spareParts : [],
  remarks: entry?.inspection?.comments || '',
  preparedBy: entry?.inspection?.preparedBy || '',
  completedByUser: entry?.inspection?.completedByUser || '',
  completedAt: entry?.inspection?.completedAt || null,
});

const ServiceRequestCompletionModal = ({ isOpen, onClose, entry, readOnly = false, onSaved }) => {
  const { username: authUsername } = useContext(AuthContext);
  const [form, setForm] = useState(() => buildState(entry));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(buildState(entry));
  }, [isOpen, entry]);

  if (!isOpen || !entry) return null;

  const spareParts = form.spareParts.length > 0 ? form.spareParts : [blankSparePart()];
  const setField = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const setSparePart = (idx, patch) => {
    setForm((prev) => {
      const next = (prev.spareParts.length > 0 ? prev.spareParts : [blankSparePart()]).slice();
      next[idx] = { ...blankSparePart(), ...next[idx], ...patch };
      return { ...prev, spareParts: next };
    });
  };

  const addSparePart = () =>
    setForm((prev) => ({
      ...prev,
      spareParts: [...(prev.spareParts.length > 0 ? prev.spareParts : [blankSparePart()]), blankSparePart()],
    }));

  const removeSparePart = (idx) =>
    setForm((prev) => ({ ...prev, spareParts: prev.spareParts.filter((_, i) => i !== idx) }));

  const handleSave = async ({ markComplete = false } = {}) => {
    if (!entry?._id) {
      toast.error('Cannot save: missing work order id.');
      return;
    }

    const preparedBy = (form.preparedBy || '').trim();

    if (markComplete && !preparedBy) {
      toast.error('Please fill in "Prepared by" before completing.');
      return;
    }

    const existingInspection = entry?.inspection || {};

    const payload = {
      scStatus: markComplete ? 'Closed' : form.scStatus,
      targetResponse: toISO(form.targetResponse),
      targetResolution: toISO(form.targetResolution),
      actualDate: toISO(form.actualDate),
      actualResolution: toISO(form.actualResolution),
      inspection: {
        ...existingInspection,
        spareParts: (form.spareParts || []).filter(hasSparePartValue),
        comments: form.remarks,
        preparedBy,
        completedByUser: markComplete ? String(authUsername || '').trim() : form.completedByUser || '',
        completedAt: markComplete ? new Date().toISOString() : form.completedAt,
      },
    };

    try {
      setSaving(true);
      const res = await qdfAPI.updateReport(entry._id, payload);
      const updated = res?.data?.report || { ...entry, ...payload };
      toast.success(
        markComplete ? 'Service request completed and saved.' : 'Service request progress saved.'
      );
      if (onSaved) onSaved(updated);
      onClose();
    } catch (err) {
      console.error('Failed to save service request:', err);
      toast.error(err?.response?.data?.message || 'Failed to save service request.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={entry.scNumber || 'Service request'}
      subtitle={[entry.location, entry.assetTag].filter(Boolean).join(' · ')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Close
          </Button>
          {!readOnly && (
            <>
              <Button variant="secondary" onClick={() => handleSave({ markComplete: false })} loading={saving}>
                Save progress
              </Button>
              <Button variant="success" onClick={() => handleSave({ markComplete: true })} loading={saving}>
                Complete &amp; save
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border pb-4">
        <Badge variant="type" size="sm">SR</Badge>
        <Badge variant="neutral" size="sm">Corrective</Badge>
        {readOnly && <Badge variant="warning" size="sm">Read only</Badge>}
        <span className="text-sm text-muted-fg">
          Requested by <span className="font-medium text-foreground">{entry.requesterName || 'N/A'}</span>
        </span>
      </div>

      <div className="space-y-8">
        <FieldSet legend="Work order status">
          <div className="max-w-sm">
            <Select
              label="Status"
              value={form.scStatus}
              disabled={readOnly}
              onChange={(e) => setField({ scStatus: e.target.value })}
              hint="“Complete & save” marks this work order as Closed automatically."
            >
              <option value="">Select status</option>
              {SC_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
          </div>
        </FieldSet>

        <FieldSet legend="Resolution timing">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Target response"
              type="datetime-local"
              value={form.targetResponse}
              disabled={readOnly}
              onChange={(e) => setField({ targetResponse: e.target.value })}
            />
            <Input
              label="Target resolution"
              type="datetime-local"
              value={form.targetResolution}
              disabled={readOnly}
              onChange={(e) => setField({ targetResolution: e.target.value })}
            />
            <Input
              label="Actual response"
              type="datetime-local"
              value={form.actualDate}
              disabled={readOnly}
              onChange={(e) => setField({ actualDate: e.target.value })}
            />
            <Input
              label="Actual resolution"
              type="datetime-local"
              value={form.actualResolution}
              disabled={readOnly}
              onChange={(e) => setField({ actualResolution: e.target.value })}
            />
          </div>
        </FieldSet>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-fg">
              Spare parts used
            </h4>
            {!readOnly && (
              <Button variant="ghost" size="sm" icon={AddOutlined} onClick={addSparePart}>
                Add part
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {spareParts.map((part, idx) => (
              <div key={idx} className="rounded-lg border border-border bg-muted/40 p-3 sm:p-4">
                <div className="grid gap-3 sm:grid-cols-[1fr_1fr_6rem]">
                  <Input
                    label="Device name"
                    value={part.deviceName}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { deviceName: e.target.value })}
                  />
                  <Input
                    label="Description"
                    value={part.description}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { description: e.target.value })}
                  />
                  <Input
                    label="Quantity"
                    inputMode="numeric"
                    value={part.quantity}
                    disabled={readOnly}
                    onChange={(e) => setSparePart(idx, { quantity: e.target.value })}
                  />
                </div>
                {!readOnly && spareParts.length > 1 && (
                  <div className="mt-2 flex justify-end">
                    <Button variant="ghost" size="sm" icon={DeleteOutline} onClick={() => removeSparePart(idx)}>
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <FieldSet legend="Completion">
          <div className="space-y-4">
            <Textarea
              label="Remarks"
              rows={4}
              value={form.remarks}
              disabled={readOnly}
              onChange={(e) => setField({ remarks: e.target.value })}
              placeholder="Completion notes, actions taken, pending follow-ups…"
            />
            <div className="max-w-sm">
              <Input
                label="Prepared by"
                required
                value={form.preparedBy}
                disabled={readOnly}
                onChange={(e) => setField({ preparedBy: e.target.value })}
                placeholder="Technician name"
              />
              {form.completedAt && (
                <div className="mt-2 space-y-0.5 text-xs text-muted-fg">
                  <p>Completed by: {form.completedByUser || 'N/A'}</p>
                  <p>Completed: {new Date(form.completedAt).toLocaleString('en-GB')}</p>
                </div>
              )}
            </div>
          </div>
        </FieldSet>
      </div>
    </Modal>
  );
};

export default ServiceRequestCompletionModal;
